/**
 * Petit Toi "arrive by" box.
 *
 * Pure date math against the settings/cart state passed in as data
 * attributes (production days, transit days, EU/US split, saved occasion
 * and date). Persists `Arrive by occasion` / `Arrive by date` cart
 * attributes (debounced) and, when the date is "tight", offers to add one
 * rush line per doll — provided every doll line in the cart is rush-eligible.
 */
import { fetchConfig } from '@theme/utilities';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** @param {Date} date @param {number} days */
export function addDays(date, days) {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

/** @param {string} value - ISO `YYYY-MM-DD`. */
export function parseISODate(value) {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return Number.isNaN(date.getTime()) ? null : date;
}

/** @param {Date} date */
function formatDate(date) {
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * Natural-language "early by" phrase matching the K prototype's own copy
 * (`In time, about a week early`) instead of a raw day count. Only the
 * display string changes here — the day-count math it wraps is untouched.
 * @param {number} daysEarly - Always > 0 when called.
 */
function formatDaysEarly(daysEarly) {
  if (daysEarly % 7 === 0) {
    const weeks = daysEarly / 7;
    return weeks === 1 ? 'about a week early' : `about ${weeks} weeks early`;
  }
  return daysEarly === 1 ? 'about a day early' : `about ${daysEarly} days early`;
}

/** @param {() => void} fn @param {number} wait */
function debounce(fn, wait) {
  /** @type {ReturnType<typeof setTimeout>} */
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), wait);
  };
}

/**
 * Rush guard rule, shared with the product page and mirrored in
 * `pt-arrive-by.liquid` (server-rendered `can_offer_rush`) and used by
 * `pt-cart.js`'s live rush guard: a product with no `Size` option can always
 * take rush; a product with a `Size` option can only take it when
 * `custom.rush_allowed_sizes` is non-empty and includes the selected size.
 * Never throws on a missing/null size list.
 * @param {boolean} hasSizeOption
 * @param {string[] | null | undefined} allowedSizes
 * @param {string | null | undefined} size
 * @returns {boolean}
 */
export function isRushAllowed(hasSizeOption, allowedSizes, size) {
  if (!hasSizeOption) return true;
  if (!Array.isArray(allowedSizes) || allowedSizes.length === 0) return false;
  return allowedSizes.includes(size);
}

class PtArriveBy extends HTMLElement {
  /** @type {() => void} */
  #debouncedSave = () => {};

  connectedCallback() {
    // The inputs belong to the checkout form (so attributes survive a fast checkout); Enter must not submit it.
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' && event.target instanceof HTMLInputElement && event.target.form?.getAttribute('id') === 'pt-cart-form') {
        event.preventDefault();
      }
    });
    this.rangeEl = this.querySelector('[data-pt-arrive-range]');
    this.statusEl = this.querySelector('[data-pt-arrive-status]');
    this.errorEl = this.querySelector('[data-pt-arrive-error]');
    this.summaryEl = this.querySelector('[data-pt-arrive-summary]');
    this.occasionInput = this.querySelector('[data-pt-arrive-occasion]');
    this.dateInput = this.querySelector('[data-pt-arrive-date]');

    this.#debouncedSave = debounce(this.#saveAttributes.bind(this), 600);

    this.occasionInput?.addEventListener('input', () => this.#debouncedSave());
    this.dateInput?.addEventListener('change', () => {
      this.#renderStatus();
      this.#debouncedSave();
    });

    this.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;
      const button = event.target.closest('[data-pt-add-rush]');
      if (button) {
        event.preventDefault();
        this.#addRush(/** @type {HTMLButtonElement} */ (button));
      }
    });

    this.#renderRange();
    this.#renderStatus();
  }

  #transitDays() {
    return this.dataset.isEu === 'true' ? Number(this.dataset.transitEu) : Number(this.dataset.transitUs);
  }

  /** Weekly order cap: `shop.metafields.custom.order_cap_reached`, pushed out by `settings.pt_cap_extra_days`. */
  #capped() {
    return this.dataset.capReached === 'true';
  }

  #capDays() {
    // Mirrors the >= 0 clamp in pt-arrive-by.liquid: a negative or blank
    // value must never pull the "next open week" cutoff earlier than uncapped.
    return this.#capped() ? Math.max(0, Number(this.dataset.capExtraDays) || 0) : 0;
  }

  #today() {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  }

  #earliest() {
    return addDays(this.#today(), Number(this.dataset.productionMin) + this.#transitDays() + this.#capDays());
  }

  /** While capped this is the "next open week" cutoff: every date before it is late. */
  #latest() {
    return addDays(this.#today(), Number(this.dataset.productionMax) + this.#transitDays() + this.#capDays());
  }

  #renderRange() {
    if (!this.rangeEl) return;
    // While capped, #renderStatus() treats every date before `#latest()` (the
    // pushed-out "next open week") as late — there is no achievable date
    // between `#earliest()` and `#latest()` anymore, so showing that lower
    // bound here would contradict the late/in-time check below. Show only
    // the one date that actually agrees with it.
    if (this.#capped()) {
      this.rangeEl.textContent = `Arrives from ${formatDate(this.#latest())}`;
      return;
    }
    this.rangeEl.textContent = `Arrives ${formatDate(this.#earliest())} – ${formatDate(this.#latest())}`;
  }

  #renderStatus() {
    if (!this.statusEl) return;
    this.statusEl.classList.remove('pt-arrive-by__status--in-time', 'pt-arrive-by__status--tight', 'pt-arrive-by__status--late');
    this.#setError('');

    const chosen = parseISODate(this.dateInput instanceof HTMLInputElement ? this.dateInput.value : '');
    if (!chosen) {
      this.statusEl.textContent = '';
      return;
    }

    const capped = this.#capped();
    const earliest = this.#earliest();
    const latest = this.#latest();
    // Rush is never offered while capped, and the "tight" window collapses into "late" (`latest` already
    // includes `pt_cap_extra_days`, i.e. the next open week), so every date before it reads as late.
    const canOfferRush = !capped && this.dataset.canOfferRush === 'true';

    if (chosen.getTime() >= latest.getTime()) {
      this.statusEl.classList.add('pt-arrive-by__status--in-time');
      const daysEarly = Math.round((chosen.getTime() - latest.getTime()) / MS_PER_DAY);
      this.statusEl.textContent = daysEarly > 0 ? `In time, ${formatDaysEarly(daysEarly)}` : 'In time';
    } else if (!capped && chosen.getTime() >= earliest.getTime()) {
      this.statusEl.classList.add('pt-arrive-by__status--tight');
      this.statusEl.textContent = 'Cutting it close for that date.';
      if (canOfferRush) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'btn ghost pt-arrive-by__rush-button';
        button.dataset.ptAddRush = 'true';
        button.textContent = 'Add rush production';
        this.statusEl.append(' ', button);
      }
    } else {
      this.statusEl.classList.add('pt-arrive-by__status--late');
      this.statusEl.textContent = "This likely won't arrive by then. Consider a gift card note for now, with the doll to follow.";
    }

    if (this.summaryEl) {
      const occasion = this.occasionInput instanceof HTMLInputElement ? this.occasionInput.value : '';
      const date = this.dateInput instanceof HTMLInputElement ? this.dateInput.value : '';
      if (occasion || date) {
        const parts = [occasion, date].filter(Boolean).join(' · ');
        const bold = document.createElement('b');
        bold.textContent = parts;
        this.summaryEl.replaceChildren('Needed by ', bold, ' · edit');
      }
    }
  }

  /**
   * Writes to a node separate from `statusEl` so an error never overwrites
   * the in-time/tight/late text or, critically, the "Add rush" button that
   * `#renderStatus()` appends inside `statusEl`.
   * @param {string} message
   */
  #setError(message) {
    if (this.errorEl) this.errorEl.textContent = message || '';
  }

  async #saveAttributes() {
    const occasion = this.occasionInput instanceof HTMLInputElement ? this.occasionInput.value : '';
    const date = this.dateInput instanceof HTMLInputElement ? this.dateInput.value : '';

    const cart = await fetch('/cart.js').then((response) => response.json());
    const attributes = {
      ...cart.attributes,
      'Arrive by occasion': occasion,
      'Arrive by date': date,
    };

    await fetch(
      Theme.routes.cart_update_url,
      fetchConfig('json', { body: JSON.stringify({ attributes }) })
    );
  }

  /** @param {HTMLButtonElement} button */
  async #addRush(button) {
    const variantId = this.dataset.rushVariantId;
    const dollIds = (this.dataset.missingRushDollIds || '').split(',').filter(Boolean);
    if (!variantId || dollIds.length === 0) return;

    button.disabled = true;
    try {
      const items = dollIds.map((dollId) => ({
        id: Number(variantId),
        quantity: 1,
        properties: { _for_doll: dollId },
      }));

      const response = await fetch(
        Theme.routes.cart_add_url,
        fetchConfig('json', { body: JSON.stringify({ items }) })
      );
      const addData = await response.json();

      if (!response.ok || addData.status) {
        this.#setError(addData.description || addData.message || "Couldn't add rush production — please try again.");
        button.disabled = false;
        return;
      }

      this.#setError('');
      // /cart/add.js's response shape lacks item_count/total_price; fetch a
      // fresh /cart.js so the header bubble update carries the real cart.
      const cart = await fetch('/cart.js').then((res) => res.json());
      this.dispatchEvent(new CustomEvent('pt:cart-mutated', { bubbles: true, detail: { data: cart } }));
    } catch (error) {
      console.warn('[pt-arrive-by]', error);
      this.#setError("Couldn't add rush production — please try again.");
      button.disabled = false;
    }
  }
}

if (!customElements.get('pt-arrive-by')) {
  customElements.define('pt-arrive-by', PtArriveBy);
}
